import React, { useEffect, useState } from "react";
import { RouteChildrenProps } from "react-router";
import { FormattedDate } from "react-intl";
import moment from "moment";

import {
  AddressShape,
  CashCloseTypes,
  ContactShape,
  DocShape,
  DocTypes,
  humanizedCashCloseTypes,
} from "@eave/shared/types/api";
import { Address } from "@eave/shared/components/Address";

import { getFileDownloadBaseURL } from "config";
import APIClient from "web_modules/APIClient";
import LabeledStructuredList from "web_modules/shared/LabeledStructuredList";
import LabeledObject from "web_modules/shared/LabeledObject";
import BaseTable from "web_modules/shared/BaseTable";
import { docTypeToHumanStr } from "web_modules/underwriter_dashboard/shared";
import ConditionsTable from "./ConditionsTable";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

const styles = require("./styles.module.scss");

interface SettlementAgentApplication {
  id: number;
  purchased_property_address: AddressShape;
  disbursement_date: string;
  borrower1_first_name: string;
  borrower1_middle_name?: string;
  borrower1_last_name: string;
  borrower1_suffix?: string;
  borrower1_email: string;
  borrower2_first_name: string;
  borrower2_middle_name?: string;
  borrower2_last_name: string;
  borrower2_suffix?: string;
  borrower2_email: string;
  documents: DocShape[];
  closing_date: string;
  signing_date: string;
  external_id: string | null;
  cash_close_type: CashCloseTypes;
  cash_trade_in: CashTradeIn;
  loan_manager: ContactShape | null;
  hl_order_id: number | null;
}

interface CashTradeIn {
  id: number;
  hl_order_id: number;
  created_at: string;
  updated_at: string;
  stage: CashTradeInStages;
  departing_residence_address: string;
  departing_residence_preliminary_home_value: string | null;
  departing_residence_home_valuation: string | null;
  departing_residence_escrow_ordered: string | null;
  departing_residence_guaranteed_sale_price: string | null;
  departing_residence_buy_commission: string | null;
  departing_residence_sell_commission: string | null;
  departing_residence_cash_close_fee: string | null;
  departing_residence_ownership_expenses: string | null;
  departing_residence_transfer_tax: string | null;
  departing_residence_listing_prep_fee: string | null;
  departing_residence_disbursement_date: string | null;
  incoming_residence_address: string;
}

enum CashTradeInStages {
  New = "new",
  Connected = "connected",
  ApplicationStarted = "application_started",
  ApplicationComplete = "application_complete",
  InspectionComplete = "inspection_complete",
  Approved = "approved",
  AgreementSigned = "agreement_signed",
  InEscrowPurchase = "in_escrow_purchase",
  InEscrowSell = "in_escrow_sell",
  ClosedPurchase = "closed_purchase",
  ClosedSell = "closed_sell",
  ClosedPaid = "closed_paid",
  Listed = "listed",
  Failed = "failed",
  Nurture = "nurture",
}

const getFullName = names => {
  return names.join(" ").trim();
};

const getAddress = (app: SettlementAgentApplication) => {
  return {
    line1: app.purchased_property_address && app.purchased_property_address.line1,
    line2: app.purchased_property_address && app.purchased_property_address.line2,
    city: app.purchased_property_address && app.purchased_property_address.city,
    state: app.purchased_property_address && app.purchased_property_address.state,
    zip: app.purchased_property_address && app.purchased_property_address.postal_code.split("-")[0],
  };
};

const documentName = (doc: DocShape): React.ReactNode => {
  switch (doc.type) {
    case DocTypes.BorrowersAuthorization:
      return (
        <>
          <div>Seller&apos;s Certification and Authorization</div>
          <div className={styles.italicized}>
            filename: {doc.filename} / date: {moment(doc.created_at).format("MM/DD/YYYY")}
          </div>
        </>
      );
    default:
      return (
        <>
          <div>{docTypeToHumanStr(doc.type)}</div>
          <div className={styles.italicized}>
            filename: {doc.filename} / date: {moment(doc.created_at).format("MM/DD/YYYY")}
          </div>
        </>
      );
  }
};

const AppViewContainer: React.FC<RouteChildrenProps<{ id: string }>> = props => {
  const {
    match: {
      params: { id },
    },
  } = props;

  const [appData, setAppData] = useState<SettlementAgentApplication | null>(null);
  useEffect(() => {
    APIClient.get(`/sa/loan_applications/${id}`)
      .then(resp => {
        const { application } = resp as {
          application: SettlementAgentApplication;
        };
        setAppData(application);
      })
      .catch(() => {
        window.alert(`There was an error fetching loan app id: ${id}. Please contact eng.`);
      });
  }, [id]);

  const getApplicantSummaryData = ({
    borrower1_first_name,
    borrower1_middle_name,
    borrower1_last_name,
    borrower1_suffix,
    borrower1_email,
    borrower2_first_name,
    borrower2_middle_name,
    borrower2_last_name,
    borrower2_suffix,
    borrower2_email,
  }: SettlementAgentApplication) => {
    const b1 = [
      {
        borrower_entity: 1,
        full_name: getFullName([
          borrower1_first_name,
          borrower1_middle_name,
          borrower1_last_name,
          borrower1_suffix,
        ]),
        email: <a href={`mailto:${borrower1_email}`}>{borrower1_email}</a>,
      },
    ];

    return borrower2_first_name === ""
      ? b1
      : [
          ...b1,
          {
            borrower_entity: 2,
            full_name: getFullName([
              borrower2_first_name,
              borrower2_middle_name,
              borrower2_last_name,
              borrower2_suffix,
            ]),
            email: <a href={`mailto:${borrower2_email}`}>{borrower2_email}</a>,
          },
        ];
  };

  if (!appData) {
    return <p>fetching data ...</p>;
  }

  const {
    loan_manager,
    purchased_property_address,
    documents,
    external_id,
    disbursement_date,
    cash_close_type,
    cash_trade_in,
    hl_order_id,
  } = appData;

  return (
    <div className={styles.singleAppPage}>
      <LabeledStructuredList
        title="DR Seller / IR Applicant Summary"
        tooltip="DR is an abbreviation for Departing Residence and IR is an abbreviation for Incoming Residence."
        colHeaders={[
          {
            displayName: "DR Seller / IR Borrower #",
            dataKey: "borrower_entity",
          },
          {
            displayName: "Full name",
            dataKey: "full_name",
          },
          {
            displayName: "Email",
            dataKey: "email",
          },
        ]}
        rowData={getApplicantSummaryData(appData)}
      />
      <div className={styles.summaryContainer}>
        <div className={styles.cashCloseWrapper}>
          <LabeledObject
            title="Cash Close Summary"
            listData={[
              {
                label: "Cash close product",
                value: humanizedCashCloseTypes[cash_close_type] || cash_close_type || (
                  <span>&mdash;</span>
                ),
              },
              {
                label: "HL Order ID",
                value: hl_order_id ? hl_order_id.toString() : <span>&mdash;</span>,
              },
              {
                label: "Departing Residence (DR)",
                value: cash_trade_in ? (
                  cash_trade_in.departing_residence_address
                ) : (
                  <span>&mdash;</span>
                ),
                tooltip:
                  "Departing Residence (DR) is the residence that the client will be selling in order to purchase the Incoming Residence (IR)",
              },
              {
                label: "DR disbursement date",
                tooltip:
                  "Departing Residence (DR) disbursement date is the close of escrow date of the borrower's residence that is being sold",
                value: cash_trade_in.departing_residence_disbursement_date ? (
                  cash_trade_in.departing_residence_disbursement_date
                ) : (
                  <span>&mdash;</span>
                ),
              },
            ]}
          />
        </div>
        <div className={styles.incomingResidenceWrapper}>
          <LabeledObject
            title="Incoming Residence (IR) Summary"
            listData={[
              {
                label: "Loan manager",
                value: loan_manager && loan_manager.name ? loan_manager.name : <span>&mdash;</span>,
              },
              {
                label: "Loan manager email",
                value:
                  loan_manager && loan_manager.email ? (
                    <a href={`mailto:${loan_manager.email}`}>{loan_manager.email}</a>
                  ) : (
                    <span>&mdash;</span>
                  ),
              },
              {
                label: "Loan number",
                value: external_id || <span>&mdash;</span>,
              },
              {
                label: "Incoming Residence (IR)",
                value:
                  purchased_property_address && purchased_property_address.line1 ? (
                    <Address address={getAddress(appData)} />
                  ) : (
                    <span>&mdash;</span>
                  ),
                tooltip:
                  "Incoming Residence (IR) is the residence that the client will be purchasing with funds from the Departing Residence (DR)",
              },
              {
                label: "IR signing date",
                value: appData.signing_date ? (
                  <FormattedDate
                    value={appData.signing_date}
                    year="numeric"
                    month="numeric"
                    day="numeric"
                    timeZone="UTC"
                  />
                ) : (
                  <span>&mdash;</span>
                ),
                tooltip: "Incoming Residence (IR) signing date of the borrower's new residence",
              },
              {
                label: "IR closing date",
                value: appData.closing_date ? (
                  <FormattedDate
                    value={appData.closing_date}
                    year="numeric"
                    month="numeric"
                    day="numeric"
                    timeZone="UTC"
                  />
                ) : (
                  <span>&mdash;</span>
                ),
                tooltip:
                  "Incoming Residence (IR) closing date is the Close of Escrow / Recording date of the borrower's new residence",
              },
              {
                label: "IR disbursement date",
                value: disbursement_date ? (
                  <FormattedDate
                    value={disbursement_date}
                    year="numeric"
                    month="numeric"
                    day="numeric"
                    timeZone="UTC"
                  />
                ) : (
                  <span>&mdash;</span>
                ),
                tooltip:
                  "Incoming Residence (IR) disbursement date is the funding date of the borrower's new residence",
              },
            ]}
          />
        </div>
      </div>
      <ConditionsTable appID={id} />
      <BaseTable
        openModal={() => {}}
        disableAllControls
        title="Documents"
        headerData={makeHeaderData({
          document: "Document",
          link: "Download",
        })}
        rowData={documents.map(document => ({
          id: document.id.toString(),
          document: <td key={`${document.id}_name`}>{documentName(document)}</td>,
          link: (
            <td key={`${document.id}_link`}>
              <a
                download
                href={getFileDownloadBaseURL(document.s3_key)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </a>
            </td>
          ),
        }))}
      />
    </div>
  );
};

export default AppViewContainer;
